import React, { useEffect, useState } from "react";
import { Key } from '@react-types/shared';
import { CountryCode } from "libphonenumber-js";
import awesomeNumber from "awesome-phonenumber";
import { PhoneInput } from "@boi/react-components";
import { countriesData } from "@boi/react-country-assets";

import Container from "../forms/controls/container";
import { FormContext } from "../forms";
import { iControlProps } from "../forms/types";

type BoiPhoneProps = iControlProps & {
    countriesBoiPhone: CountryCode[];
    id: string;
    onChange: (value: any) => void;
    value: [string, CountryCode] | null;
    name: string;
    type: string;
    label: string;
    phoneTypeCheck: { fixed_line: boolean; mobile: boolean };
    format: string;
    isRequired: boolean;
    context: FormContext;
    size: string;
    error?: string;
    placeholder?: string;
};

const BoiPhone = ({
    placeholder,
    countriesBoiPhone,
    size,
    value,
    format,
    ...props
}: BoiPhoneProps) => {
    const numberInput = value?.length ? value[0] : "";
    const regionCode = value?.length ? value[1] : "IE";
    const parsedPhoneNumber = awesomeNumber.parsePhoneNumber(numberInput, {
        regionCode: regionCode
    });

    const [countryCode, setCountryCode] = useState<CountryCode>(
        (parsedPhoneNumber?.regionCode as CountryCode) || ""
    );
    const [callingCode, setCallingCode] = useState(regionCode);
    const [phoneNumber, setPhoneNumber] = useState(parsedPhoneNumber?.number?.significant);
    const [error, setError] = useState(props.error);

    useEffect(() => {
        setError(props.error);
    }, [props.error]);

    const handleCountryCodeChange = (value: Key | null) => {
        const selectedCountry = countriesData.find((country) => country.code === value);
        //@ts-ignore
        const { code = "IE", callingCode = "+353"} = selectedCountry;
        setCallingCode(callingCode);
        setCountryCode(code);

        if (phoneNumber) {
            const parsedPhoneNumber = awesomeNumber.parsePhoneNumber(
                phoneNumber,
                {
                    regionCode: code
                }
            );
            let numberInput = phoneNumber;

            if (parsedPhoneNumber.number && parsedPhoneNumber.number.input) {
                numberInput =
                    (format === "international"
                        ? parsedPhoneNumber.number.international
                        : parsedPhoneNumber.number.national
                    )?.replaceAll(/[\s()]/g, "") || callingCode + phoneNumber;
            }

            props.onChange &&
                props.onChange(numberInput ? [numberInput, code] : null);
        }
    }

    const handleInputChange = (phoneNumber: string) => {
        const parsedPhoneNumber = awesomeNumber.parsePhoneNumber(phoneNumber, {
            regionCode: countryCode
        });
        let numberInput = "";

        setPhoneNumber(phoneNumber);
        if (parsedPhoneNumber.number && parsedPhoneNumber.number.input) {
            numberInput =
                (format === "international"
                    ? parsedPhoneNumber.number.international
                    : parsedPhoneNumber.number.national
                )?.replaceAll(/[\s()]/g, "") || callingCode + phoneNumber;
        }

        props.onChange &&
            props.onChange(numberInput ? [numberInput, countryCode || "IE"] : null);
    }

    const handleOnBlur = () => {
        setError(props.error);
    };

    const handleOnFocus = () => {
        setError("");
    };

    function isNotNullish<T>(value: T | undefined | null): value is T {
        return value !== undefined && value !== null
    }
    
    const allowedCountries = countriesData.map(
        ({ code, label, callingCode, component: FlagComponent }) => {
            if (countriesBoiPhone.includes(code as CountryCode)) {
                return {
                    id: code,
                    callingCode,
                    label,
                    flag: <><FlagComponent /></>
                }
            }
        }
    ).filter(isNotNullish);

    const accessibilitySettings: { [key: string]: string } = {};
    props.accessibilitySettings &&
        props.accessibilitySettings.forEach(setting => {
            if (setting.isShown)
                accessibilitySettings[setting.name] = setting.value || "";
        });
    
    return (
        <Container {...props} error={error}>
            <span>
                <PhoneInput aria-label={props.label}>
                    <PhoneInput.Country
                        defaultSelectedKey={regionCode}
                        items={allowedCountries}
                        onSelectionChange={handleCountryCodeChange}
                        style={{ order: 0 }}
                    />
                    <PhoneInput.Number
                        id={props.id}
                        placeholder={placeholder || "Enter phone number"}
                        isRequired={props.isRequired}
                        isInvalid={!!props.error}
                        onChange={handleInputChange}
                        onBlur={handleOnBlur}
                        onFocus={handleOnFocus}
                        value={phoneNumber}
                        {...accessibilitySettings}
                    />
                </PhoneInput>
            </span>
        </Container>
    );
};

export default BoiPhone;
